import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { SiteSetting } from '../types/api';
import {
  Cog6ToothIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  WrenchScrewdriverIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';

const categoryIcons: Record<string, React.ComponentType<any>> = {
  general: GlobeAltIcon,
  contact: EnvelopeIcon,
  seo: ChartBarIcon,
  analytics: ChartBarIcon,
  uploads: CloudArrowUpIcon,
  system: WrenchScrewdriverIcon,
  social: ShareIcon,
};

const categoryNames: Record<string, string> = {
  general: 'General',
  contact: 'Contact',
  seo: 'SEO',
  analytics: 'Analytics',
  uploads: 'File Uploads',
  system: 'System',
  social: 'Social Media',
};

const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<SiteSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSetting, setEditingSetting] = useState<SiteSetting | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await apiService.getSettings();
      setSettings(data);
      setError(null);
    } catch (err) {
      setError('Failed to load settings');
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting: SiteSetting) => {
    setEditingSetting(setting);
  };

  const handleSave = async (setting: SiteSetting, newValue: string) => {
    try {
      setSaving(true);
      await apiService.updateSetting(setting.id, { value: newValue });
      
      // Update the local state
      setSettings(prev => prev.map(s => 
        s.id === setting.id ? { ...s, value: newValue } : s
      ));
      
      setEditingSetting(null);
      setError(null);
    } catch (err) {
      setError('Failed to update setting');
      console.error('Error updating setting:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingSetting(null);
  };

  const groupSettingsByCategory = () => {
    const grouped: Record<string, SiteSetting[]> = {};
    settings.forEach(setting => {
      if (!grouped[setting.category]) {
        grouped[setting.category] = [];
      }
      grouped[setting.category].push(setting);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div 
          className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
          role="status"
          aria-label="Loading settings"
        ></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  const groupedSettings = groupSettingsByCategory();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure global settings for your website
          </p>
        </div>
      </div>

      <div className="space-y-8">
        {Object.entries(groupedSettings).map(([category, categorySettings]) => {
          const IconComponent = categoryIcons[category] || Cog6ToothIcon;
          const categoryName = categoryNames[category] || category;

          return (
            <div key={category} className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <IconComponent className="h-5 w-5 text-gray-400 mr-2" />
                  <h2 className="text-lg font-medium text-gray-900">{categoryName}</h2>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {categorySettings.map((setting) => (
                  <div key={setting.id} className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h3 className="text-sm font-medium text-gray-900">
                            {setting.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </h3>
                          {setting.is_public && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Public
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-gray-500">{setting.description}</p>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        {editingSetting?.id === setting.id ? (
                          <div className="flex items-center space-x-2">
                            <input
                              ref={inputRef}
                              type="text"
                              defaultValue={setting.value}
                              className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const target = e.target as HTMLInputElement;
                                  handleSave(setting, target.value);
                                } else if (e.key === 'Escape') {
                                  handleCancel();
                                }
                              }}
                              autoFocus
                            />
                            <button
                              onClick={() => {
                                if (inputRef.current) {
                                  handleSave(setting, inputRef.current.value);
                                }
                              }}
                              disabled={saving}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancel}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-900 max-w-xs truncate">
                              {setting.value || '(empty)'}
                            </span>
                            <button
                              onClick={() => handleEdit(setting)}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SettingsPage;
